/**
 * Filter + bulk payload builders for modular price-list property edits.
 */
import { cloneDeep } from 'lodash';
import {
    buildCategoryAssignmentsFromSystems,
    DEFAULT_SYSTEM_SUBCATEGORY,
    getProductMainCategories,
    normalizeSystemValue,
} from '@/data/productCategories';
import {
    createDraftEntry,
    resolveDraftAction,
} from '@/utils/priceListDraftQueue';
import {
    getBulkField,
    formatBulkEditValue,
    formatCurrentFieldValue,
    itemMatchesBulkFieldFilter,
    normalizeBulkEditValue,
    resolveItemMarkupRatio,
    subcategoryForSystem,
    systemsFromItem,
    getItemFurnizorFilterLabels,
} from '@/utils/priceListBulkFields';
import {
    builderFormToCalculatorInput,
    calculateProductPricing,
    DEFAULT_PRICING_SETTINGS,
    hydrateDiscountsFromLegacy,
    parseNumericInput,
    percentageInputToRatio,
    persistActiveFurnizorPricingFromRoot,
    roundMoney,
    syncSystemsListToLegacy,
    formatNumericInputValue,
} from '@/utils/priceListProductCalculator';
import {
    applyPricingFieldEdit,
} from '@/utils/priceListPricingFieldSync';
import {
    buildDraftPricingBaselinesFromPreEditForm,
    mergeDraftPricingBaselinesWithExisting,
} from '@/utils/priceListPricingFieldBaselines';
import {
    builderFormToSavePayload,
    legacyProductToBuilderForm,
    pricingDetailToBuilderForm,
} from '@/utils/pricingDetailMapper';
import { parseProductCodeStructure } from '@/utils/productStructureHelpers';
import { includesFolded } from '@/utils/textSearch';
import { getManufacturerCodes } from '@/data/manufacturerCodes';
import {
    activateMatchingFurnizorTab,
    getFurnizorIdentity,
    getPricingMatchLabels,
} from '@/utils/priceListPropagateFurnizor';

/** Fields that must go through applyPricingFieldEdit after the form value is set. */
const BULK_PRICING_FIELD_EDIT_KEYS = new Set([
    'markupActual',
    'supplierListPriceNew',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'stampDutyAmount',
]);

/**
 * Activate the furnizor pricing tab that matches a display label (bulk filter).
 * @param {Record<string, unknown>} form
 * @param {unknown} furnizorLabel
 * @returns {boolean}
 */
function activateFurnizorTabByLabel(form, furnizorLabel) {
    const wanted = String(furnizorLabel || '').trim().toLowerCase();
    if (!wanted) {
        return false;
    }
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    const context = {
        manufacturerCode: form?.manufacturerCode,
        manufacturerName: form?.manufacturerName,
    };
    for (const pricing of pricings) {
        const identity = getFurnizorIdentity(pricing, context);
        const labels = getPricingMatchLabels(pricing, context);
        const matches = labels.some((label) => String(label).trim().toLowerCase() === wanted)
            || String(identity?.label || '').trim().toLowerCase() === wanted;
        if (matches && identity) {
            return Boolean(activateMatchingFurnizorTab(form, identity));
        }
    }
    return false;
}

/**
 * Resolve manufacturer code from pricing detail or product code structure.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemManufacturerCode(item) {
    const fromDetail = String(
        item?.pricing_detail?.manufacturerCode
        || item?.manufacturer_code
        || item?.manufacturerCode
        || '',
    ).trim();
    if (fromDetail) {
        return fromDetail;
    }
    const parsed = parseProductCodeStructure(item?.product_code);
    return String(parsed?.manufacturerCode || '').trim();
}

/**
 * Resolve manufacturer name from pricing detail or product fields.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemManufacturerName(item) {
    return String(
        item?.pricing_detail?.manufacturerName
        || item?.manufacturer_name
        || item?.manufacturerName
        || item?.furnizor
        || '',
    ).trim();
}

/**
 * Resolve chapter code from pricing detail or product code structure.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemChapterCode(item) {
    const fromDetail = String(
        item?.pricing_detail?.chapterCode
        || item?.chapter_code
        || item?.chapterCode
        || '',
    ).trim();
    if (fromDetail) {
        return fromDetail;
    }
    const parsed = parseProductCodeStructure(item?.product_code);
    return String(parsed?.chapterCode || '').trim();
}

/**
 * Resolve subchapter code from pricing detail or product code structure.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemSubchapterCode(item) {
    const fromDetail = String(
        item?.pricing_detail?.subchapterCode
        || item?.subchapter_code
        || item?.subchapterCode
        || '',
    ).trim();
    if (fromDetail) {
        return fromDetail;
    }
    const parsed = parseProductCodeStructure(item?.product_code);
    return String(parsed?.subchapterCode || '').trim();
}

/**
 * Normalize manufacturer codes for comparison ("03" vs "3").
 * @param {unknown} code
 * @returns {string}
 */
function normalizeManufacturerCode(code) {
    const raw = String(code || '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    // Keep alphanumeric codes as-is; strip leading zeros only for pure numeric codes.
    if (/^\d+$/.test(raw)) {
        return String(Number(raw));
    }
    return raw;
}

/**
 * Normalize chapter/subchapter codes for comparison ("08" vs "8", "1" vs "01").
 * @param {unknown} code
 * @returns {string}
 */
function normalizeStructureCode(code) {
    const raw = String(code || '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    if (/^\d+$/.test(raw)) {
        return String(Number(raw));
    }
    return raw;
}

/**
 * @param {Object} item
 * @param {Record<string, unknown>} criteria
 * @returns {boolean}
 */
export function itemMatchesBulkCriteria(item, criteria = {}) {
    if (!item) {
        return false;
    }

    const search = String(criteria.search || '').trim();
    if (search) {
        const haystack = `${item.product_code || ''} ${item.product_name || item.name || ''}`;
        if (!includesFolded(haystack, search)) {
            return false;
        }
    }

    if (criteria.systems != null && criteria.systems !== '') {
        const wanted = (Array.isArray(criteria.systems) ? criteria.systems : [criteria.systems])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean);
        if (wanted.length) {
            const itemSystems = systemsFromItem(item);
            if (!wanted.some((system) => itemSystems.includes(system))) {
                return false;
            }
        }
    }

    if (criteria.systemSubcategory != null && String(criteria.systemSubcategory).trim()) {
        const wantedSub = String(criteria.systemSubcategory).trim().toLowerCase();
        const filterSystems = (Array.isArray(criteria.systems) ? criteria.systems : [criteria.systems])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean);
        const systemsToCheck = filterSystems.length ? filterSystems : systemsFromItem(item);
        const matched = systemsToCheck.some((system) => {
            const sub = subcategoryForSystem(item, system);
            return String(sub || '').trim().toLowerCase() === wantedSub;
        });
        if (!matched) {
            return false;
        }
    }

    if (criteria.measurementUnit != null && String(criteria.measurementUnit).trim()) {
        if (
            String(item.measurement_unit || '').trim()
            !== String(criteria.measurementUnit).trim()
        ) {
            return false;
        }
    }

    if (criteria.manufacturerCode != null && String(criteria.manufacturerCode).trim()) {
        const wanted = normalizeManufacturerCode(criteria.manufacturerCode);
        const itemCode = normalizeManufacturerCode(resolveItemManufacturerCode(item));
        if (!itemCode || itemCode !== wanted) {
            return false;
        }
    }

    if (criteria.manufacturerName != null && String(criteria.manufacturerName).trim()) {
        const name = resolveItemManufacturerName(item);
        if (!includesFolded(name, criteria.manufacturerName)) {
            return false;
        }
    }

    if (criteria.furnizorLabel != null && String(criteria.furnizorLabel).trim()) {
        const wanted = String(criteria.furnizorLabel).trim().toLowerCase();
        const labels = getItemFurnizorFilterLabels(item).map(
            (label) => String(label).trim().toLowerCase(),
        );
        if (!labels.includes(wanted)) {
            return false;
        }
    }

    if (criteria.chapterCode != null) {
        const wantedChapters = (
            Array.isArray(criteria.chapterCode) ? criteria.chapterCode : [criteria.chapterCode]
        )
            .map((entry) => normalizeStructureCode(entry))
            .filter(Boolean);
        if (wantedChapters.length) {
            const chapter = normalizeStructureCode(resolveItemChapterCode(item));
            if (!chapter || !wantedChapters.includes(chapter)) {
                return false;
            }
        }
    }

    if (criteria.subchapterCode != null && String(criteria.subchapterCode).trim()) {
        const wanted = normalizeStructureCode(criteria.subchapterCode);
        const subchapter = normalizeStructureCode(resolveItemSubchapterCode(item));
        if (!subchapter || subchapter !== wanted) {
            return false;
        }
    }

    const handledKeys = new Set([
        'search',
        'systems',
        'systemSubcategory',
        'measurementUnit',
        'manufacturerCode',
        'manufacturerName',
        'furnizorLabel',
        'chapterCode',
        'subchapterCode',
    ]);
    for (const [key, wanted] of Object.entries(criteria)) {
        if (handledKeys.has(key)) {
            continue;
        }
        const field = getBulkField(key);
        if (!field) {
            continue;
        }
        if (!itemMatchesBulkFieldFilter(item, field, wanted, criteria)) {
            return false;
        }
    }

    return true;
}

/**
 * @param {Array<Object>} items
 * @param {Record<string, unknown>} criteria
 * @returns {Array<Object>}
 */
export function filterItemsForBulkEdit(items, criteria = {}) {
    return (items || []).filter((item) => itemMatchesBulkCriteria(item, criteria));
}

/**
 * @param {Object} form
 * @param {Object} field
 * @param {unknown} normalizedValue
 */
function applyFieldToBuilderForm(form, field, normalizedValue) {
    switch (field.key) {
        case 'productName':
            form.productName = normalizedValue;
            break;
        case 'manufacturerProductName':
            form.manufacturerProductName = normalizedValue;
            (form.furnizorPricings || []).forEach((pricing) => {
                pricing.manufacturerProductName = normalizedValue;
            });
            break;
        case 'manufacturerProductCode':
            form.manufacturerProductCode = normalizedValue;
            (form.furnizorPricings || []).forEach((pricing) => {
                pricing.manufacturerProductCode = normalizedValue;
            });
            break;
        case 'measurementUnit':
            form.measurementUnit = normalizedValue;
            break;
        case 'weight':
            form.weight = normalizedValue;
            break;
        case 'productNotes':
            form.productNotes = normalizedValue;
            break;
        case 'nomenclatureCode':
            form.nomenclatureCode = normalizedValue;
            break;
        case 'serialNumber':
            form.serialNumber = Boolean(normalizedValue);
            break;
        case 'isCompositeProduct':
            form.isCompositeProduct = Boolean(normalizedValue);
            break;
        case 'visibleInOfferApp':
            form.visibleInOfferApp = Boolean(normalizedValue);
            break;
        case 'status':
            form.status = normalizedValue || null;
            break;
        case 'systems': {
            const systems = Array.isArray(normalizedValue) ? normalizedValue : [];
            form.systems = systems.length ? [...systems] : [''];
            form.systemSubcategories = form.systems.map(
                (_system, index) => form.systemSubcategories?.[index]
                    || DEFAULT_SYSTEM_SUBCATEGORY,
            );
            syncSystemsListToLegacy(form);
            break;
        }
        case 'systemSubcategory': {
            const subcategory = String(normalizedValue || '').trim();
            const systems = Array.isArray(form.systems) && form.systems.length
                ? form.systems
                : [''];
            form.systemSubcategories = systems.map(() => subcategory || DEFAULT_SYSTEM_SUBCATEGORY);
            syncSystemsListToLegacy(form);
            break;
        }
        case 'chapterCode':
            form.chapterCode = normalizedValue;
            break;
        case 'subchapterCode':
            form.subchapterCode = normalizedValue;
            break;
        case 'manufacturerCode': {
            form.manufacturerCode = normalizedValue;
            const manufacturers = getManufacturerCodes();
            const match = manufacturers.find(
                (entry) => String(entry.code).trim() === String(normalizedValue || '').trim(),
            );
            if (match?.name) {
                form.manufacturerName = match.name;
            }
            break;
        }
        case 'markupReference':
            form.markupReference = normalizedValue;
            break;
        case 'markupActual':
            form.markupActual = normalizedValue;
            break;
        case 'transportCostRate':
            form.transportCostRate = normalizedValue;
            break;
        case 'transportCostEstimated':
            form.transportCostEstimated = normalizedValue;
            break;
        case 'discount1':
            form.discount1 = normalizedValue;
            hydrateDiscountsFromLegacy(form);
            break;
        case 'discount2':
            form.discount2 = normalizedValue;
            hydrateDiscountsFromLegacy(form);
            break;
        case 'cashDiscount':
            form.cashDiscount = normalizedValue;
            hydrateDiscountsFromLegacy(form);
            break;
        case 'marketingDiscount':
            form.marketingDiscount = normalizedValue;
            break;
        case 'currency':
            form.currency = normalizedValue;
            break;
        case 'supplierListPriceOld':
            form.supplierListPriceOld = normalizedValue;
            break;
        case 'supplierListPriceNew':
            form.supplierListPriceNew = normalizedValue;
            break;
        case 'paymentTermDays':
            form.paymentTermDays = normalizedValue;
            break;
        case 'notes':
            form.notes = normalizedValue;
            break;
        case 'discountNotes':
            form.discountNotes = normalizedValue ? [normalizedValue] : [];
            break;
        case 'stampDutyAmount':
            form.stampDutyAmount = normalizedValue;
            break;
        default:
            break;
    }
}

/**
 * Policy A: markup drives final price — clear actual markup + reference/override.
 * @param {Object} form
 */
export function applyMarkupRecalcPolicyA(form) {
    form.markupActual = '';
    form.listPriceRonIncVatReference = '';
    form.overrideListPriceRonIncVatFinal = false;
    form.overrideListPriceRonIncVatFinalValue = '';
    form.useManualFinalPrice = false;
    form.manualListPriceRonIncVatFinal = '';
    form.priceAdjustmentRatio = '';
}

/**
 * Normalize bulk edit entries from either a single field/value or an edits array.
 * @param {Object|null} field
 * @param {unknown} value
 * @param {Array<{ fieldKey?: string, field?: Object, value: unknown }>|null} edits
 * @returns {Array<{ field: Object, value: unknown, normalizedValue: unknown }>|null}
 */
function resolveBulkEdits(field, value, edits = null) {
    const rawEdits = Array.isArray(edits) && edits.length
        ? edits
        : (field ? [{ field, value }] : []);
    const resolved = [];
    for (const entry of rawEdits) {
        let resolvedField = entry.field || getBulkField(entry.fieldKey);
        if (!resolvedField) {
            return null;
        }
        let applyMode = entry.applyMode === 'multiplyPercent' ? 'multiplyPercent' : 'set';
        // Dedicated "Majorare" → Majorare costuri (percent). Never touch pret lista.
        if (
            resolvedField.key === 'supplierListPriceMajorare'
            || resolvedField.appliesCostUpliftPercent
        ) {
            applyMode = 'costUpliftPercent';
        } else if (resolvedField.fixedApplyMode === 'multiplyPercent') {
            // Legacy remap: multiply % applied on a target pricing field.
            const targetKey = resolvedField.multiplyTargetFieldKey || 'supplierListPriceNew';
            const targetField = getBulkField(targetKey) || resolvedField;
            resolvedField = targetField;
            applyMode = 'multiplyPercent';
        }
        resolved.push({
            field: resolvedField,
            value: entry.value,
            normalizedValue: normalizeBulkEditValue(resolvedField, entry.value),
            applyMode,
        });
    }
    return resolved.length ? resolved : null;
}

/**
 * Append a percent Majorare costuri row (builder display %, type=percent).
 * Does not change Preț listă furnizor.
 * @param {Object} form
 * @param {unknown} percentValue user-entered percent points (e.g. 10 for +10%)
 */
export function applyCostUpliftPercent(form, percentValue) {
    const pct = parseNumericInput(percentValue);
    if (!(pct > 0)) {
        return;
    }
    if (!Array.isArray(form.costUplifts)) {
        form.costUplifts = [];
    }
    if (!Array.isArray(form.costUpliftTypes)) {
        form.costUpliftTypes = [];
    }
    if (!Array.isArray(form.costUpliftNotes)) {
        form.costUpliftNotes = [];
    }
    // Align lengths before push (older rows may miss types/notes).
    while (form.costUpliftTypes.length < form.costUplifts.length) {
        form.costUpliftTypes.push('fixed');
    }
    while (form.costUpliftNotes.length < form.costUplifts.length) {
        form.costUpliftNotes.push('');
    }
    form.costUplifts.push(formatNumericInputValue(pct, 2));
    form.costUpliftTypes.push('percent');
    form.costUpliftNotes.push('');
}

/**
 * Apply a relative % change to supplier list price (new = old * (1 + pct/100)).
 * Used only when editing "Preț listă furnizor" with applyMode multiplyPercent —
 * NOT for the dedicated Majorare bulk field.
 * @param {Object} form
 * @param {unknown} percentValue user-entered percent points (e.g. 10 for +10%)
 */
function applySupplierListPriceMultiplyPercent(form, percentValue) {
    const current = parseNumericInput(form.supplierListPriceNew);
    const pct = parseNumericInput(percentValue);
    const next = roundMoney(current * (1 + (pct / 100)), 4);
    form.supplierListPriceOld = current > 0
        ? formatNumericInputValue(current, 4)
        : form.supplierListPriceOld;
    form.supplierListPriceNew = formatNumericInputValue(next, 4);
}

/**
 * Snapshot pre-edit pricing baselines so bulk drafts reopen in builder with
 * orange highlights and Valoare veche (same shape as manual draft save).
 * @param {Object} preEditForm
 * @param {Object} pricingSettings
 * @param {Record<string, unknown>|null} [existingBuilderState]
 * @returns {ReturnType<typeof buildDraftPricingBaselinesFromPreEditForm>}
 */
export function buildBulkDraftPricingBaselines(
    preEditForm,
    pricingSettings,
    existingBuilderState = null,
) {
    const fresh = buildDraftPricingBaselinesFromPreEditForm(
        preEditForm,
        pricingSettings,
    );
    return mergeDraftPricingBaselinesWithExisting(fresh, existingBuilderState);
}

/**
 * Append an effective-date / majorare note to furnizor notes without wiping existing text.
 * @param {Object} form
 * @param {string} note
 */
function appendEffectiveNoteToForm(form, note) {
    const addition = String(note || '').trim();
    if (!addition) {
        return;
    }
    const existing = String(form.notes || '').trim();
    if (!existing) {
        form.notes = addition;
        return;
    }
    if (existing.includes(addition)) {
        return;
    }
    form.notes = `${existing}\n${addition}`;
}

/**
 * @param {Object} item
 * @param {Object|null} field
 * @param {unknown} value
 * @param {Object} options
 * @returns {{ ok: true, payload: Object, builderState: Object, preview: Object }
 *   | { ok: false, reason: string, item: Object }}
 */
export function buildBulkUpdateForItem(item, field, value, options = {}) {
    const {
        priceListIds = [],
        pricingSettings = DEFAULT_PRICING_SETTINGS,
        edits = null,
        effectiveNote = '',
        furnizorLabel = '',
    } = options;

    if (!item?.product_code) {
        return { ok: false, reason: 'Produs fără cod.', item };
    }

    const resolvedEdits = resolveBulkEdits(field, value, edits);
    if (!resolvedEdits && !String(effectiveNote || '').trim()) {
        return { ok: false, reason: 'Nicio proprietate de actualizat.', item };
    }
    const editsToApply = resolvedEdits || [];
    for (const entry of editsToApply) {
        if (!entry.field?.editable) {
            return { ok: false, reason: 'Câmpul nu poate fi editat în masă.', item };
        }
        if (
            entry.applyMode === 'multiplyPercent'
            && !entry.field.supportsMultiplyPercent
        ) {
            return {
                ok: false,
                reason: `Majorarea % nu e disponibilă pentru ${entry.field.label}.`,
                item,
            };
        }
        if (
            entry.applyMode === 'costUpliftPercent'
            && !(
                entry.field.key === 'supplierListPriceMajorare'
                || entry.field.appliesCostUpliftPercent
            )
        ) {
            return {
                ok: false,
                reason: `Majorare costuri nu e disponibilă pentru ${entry.field.label}.`,
                item,
            };
        }
    }

    const hasPricingDetail = Boolean(
        item.pricing_detail || item.has_pricing_detail,
    );

    const needsPricingRecalc = editsToApply.some(
        (entry) => entry.field.needsPricingRecalc || entry.field.markupRecalcPolicyA,
    );
    const hasMarkupPolicyA = editsToApply.some(
        (entry) => entry.field.markupRecalcPolicyA,
    );
    const markupPolicyEdit = [...editsToApply]
        .reverse()
        .find((entry) => entry.field.markupRecalcPolicyA) || null;

    const categories = getProductMainCategories(item);
    let productBuilderForm;
    let settings;

    if (hasPricingDetail) {
        ({ productBuilderForm, pricingSettings: settings } = pricingDetailToBuilderForm(
            item.pricing_detail,
            item,
        ));
    } else {
        // Upgrade legacy products by synthesizing a builder form and creating pricing_detail.
        ({ productBuilderForm, pricingSettings: settings } = legacyProductToBuilderForm(
            item,
            categories,
        ));
    }

    // When filtering by furnizor, edit that tab — not necessarily the default one.
    if (String(furnizorLabel || '').trim()) {
        activateFurnizorTabByLabel(productBuilderForm, furnizorLabel);
    }

    const mergedSettings = {
        ...DEFAULT_PRICING_SETTINGS,
        ...settings,
        ...pricingSettings,
    };

    const bulkDraftBaselines = buildBulkDraftPricingBaselines(
        productBuilderForm,
        mergedSettings,
    );

    for (const entry of editsToApply) {
        if (entry.applyMode === 'costUpliftPercent') {
            applyCostUpliftPercent(productBuilderForm, entry.normalizedValue || entry.value);
            // Same side-effects as builder "Adaugă majorare" (sticky list + markup sync).
            applyPricingFieldEdit(
                productBuilderForm,
                'costUplifts',
                productBuilderForm.costUplifts,
                mergedSettings,
            );
        } else if (
            entry.applyMode === 'multiplyPercent'
            && entry.field.key === 'supplierListPriceNew'
        ) {
            applySupplierListPriceMultiplyPercent(productBuilderForm, entry.value);
            applyPricingFieldEdit(
                productBuilderForm,
                'supplierListPriceNew',
                productBuilderForm.supplierListPriceNew,
                mergedSettings,
            );
        } else if (entry.field.key === 'currency') {
            // applyPricingFieldEdit expects the previous currency still on the form
            // so it can convert supplier list amounts into the target currency.
            applyPricingFieldEdit(
                productBuilderForm,
                'currency',
                entry.normalizedValue,
                mergedSettings,
            );
        } else if (BULK_PRICING_FIELD_EDIT_KEYS.has(entry.field.key)) {
            // Set the form field first, then run builder sync (e.g. adaos actual must
            // clear sticky list override so the new markup actually drives price).
            applyFieldToBuilderForm(
                productBuilderForm,
                entry.field,
                entry.normalizedValue,
            );
            applyPricingFieldEdit(
                productBuilderForm,
                entry.field.key,
                entry.normalizedValue,
                mergedSettings,
            );
        } else {
            applyFieldToBuilderForm(
                productBuilderForm,
                entry.field,
                entry.normalizedValue,
            );
        }
        if (entry.field.markupRecalcPolicyA) {
            applyMarkupRecalcPolicyA(productBuilderForm);
        }
    }

    appendEffectiveNoteToForm(productBuilderForm, effectiveNote);

    persistActiveFurnizorPricingFromRoot(productBuilderForm);

    const calculatorInput = builderFormToCalculatorInput(productBuilderForm);
    let computedProduct = calculateProductPricing(calculatorInput, mergedSettings);

    // Keep product code stable for updates.
    if (item.product_code) {
        productBuilderForm.overrideProductCode = true;
        productBuilderForm.overrideProductCodeValue = item.product_code;
        computedProduct.productCode = item.product_code;
    }

    // When supplier entry data is missing, the calculator yields 0. Fall back to the
    // current list price (scaled by old→new markup when both are known).
    if (
        needsPricingRecalc
        && parseNumericInput(computedProduct.listPriceRonIncVatFinal) <= 0
    ) {
        const currentPrice = parseNumericInput(item.price);
        if (currentPrice <= 0) {
            return {
                ok: false,
                reason: 'Lipsă preț furnizor / preț listă — nu se poate recalcula.',
                item,
            };
        }
        let nextPrice = currentPrice;
        if (markupPolicyEdit) {
            const oldMarkupRatio = resolveItemMarkupRatio(item);
            const newMarkupRatio = percentageInputToRatio(markupPolicyEdit.normalizedValue);
            if (oldMarkupRatio != null && oldMarkupRatio > -0.999999) {
                nextPrice = roundMoney(
                    currentPrice * ((1 + newMarkupRatio) / (1 + oldMarkupRatio)),
                    2,
                );
            }
        }
        computedProduct = {
            ...computedProduct,
            listPriceRonIncVatFinal: nextPrice,
        };
    }

    if (needsPricingRecalc) {
        productBuilderForm.listPriceRonIncVatReference = formatNumericInputValue(
            computedProduct.listPriceRonIncVatFinal,
            2,
        );
    }

    const categoryAssignments = buildCategoryAssignmentsFromSystems(
        Array.isArray(productBuilderForm.systems) ? productBuilderForm.systems : [],
        productBuilderForm.systemSubcategories || [],
    );

    const resolvedPriceListIds = priceListIds.length
        ? priceListIds
        : [];

    const payload = builderFormToSavePayload({
        form: productBuilderForm,
        settings: mergedSettings,
        computedProduct,
        categories: categoryAssignments.map((assignment) => assignment.main_category),
        categoryAssignments,
        priceListIds: resolvedPriceListIds,
        oldProductCode: item.product_code,
    });

    const discountableEdit = editsToApply.find(
        (entry) => entry.field.key === 'isDiscountableProduct',
    );
    if (discountableEdit) {
        payload.is_discountable_product = Boolean(discountableEdit.normalizedValue);
    }

    if (!needsPricingRecalc) {
        // Keep existing list price when only metadata changes.
        payload.price = item.price;
        if (payload.pricing_detail) {
            payload.pricing_detail.listPriceRonIncVatFinal = item.price;
            if (Array.isArray(payload.pricing_detail.furnizorPricings)) {
                payload.pricing_detail.furnizorPricings = payload.pricing_detail.furnizorPricings.map(
                    (row) => ({
                        ...row,
                        listPriceRonIncVatFinal: row.isDefault
                            ? item.price
                            : row.listPriceRonIncVatFinal,
                    }),
                );
            }
        }
    }

    const previousValueLabel = editsToApply
        .map((entry) => {
            const label = formatCurrentFieldValue(entry.field, item);
            return editsToApply.length > 1
                ? `${entry.field.label}: ${label}`
                : label;
        })
        .join('; ') || (String(effectiveNote || '').trim() ? '—' : '');
    const nextValueLabel = editsToApply
        .map((entry) => {
            let label;
            if (
                entry.applyMode === 'multiplyPercent'
                || entry.applyMode === 'costUpliftPercent'
            ) {
                label = `+${String(entry.value ?? '').trim()}%`;
            } else {
                label = formatBulkEditValue(entry.field, entry.value);
            }
            return editsToApply.length > 1
                ? `${entry.field.label}: ${label}`
                : label;
        })
        .join('; ');
    const noteLabel = String(effectiveNote || '').trim();
    const previousPrice = item.price;
    const nextPrice = payload.price;

    return {
        ok: true,
        payload,
        builderState: {
            productBuilderForm: cloneDeep(productBuilderForm),
            pricingSettings: cloneDeep(mergedSettings),
            ...bulkDraftBaselines,
        },
        preview: {
            product_code: item.product_code,
            product_name: item.product_name || item.name,
            previous_value: previousValueLabel || '—',
            next_value: [nextValueLabel, noteLabel].filter(Boolean).join('; ') || '—',
            previous_price: previousPrice,
            next_price: nextPrice,
            price_changed: Number(previousPrice) !== Number(nextPrice),
        },
        item,
        hasMarkupPolicyA,
    };
}

/**
 * @param {Object} params
 * @param {Array<Object>} params.items
 * @param {string} [params.fieldKey]
 * @param {unknown} [params.value]
 * @param {Array<{ fieldKey: string, value: unknown }>} [params.edits]
 * @param {Array<Object|number>} params.priceListIds
 * @param {Object} [params.pricingSettings]
 * @returns {{
 *   results: Array<Object>,
 *   skipped: Array<{ item: Object, reason: string }>,
 *   drafts: Array<Object>,
 *   payloads: Array<Object>,
 * }}
 */
export function buildBulkPayloads({
    items,
    fieldKey,
    value,
    edits = null,
    priceListIds,
    pricingSettings,
    effectiveNote = '',
    furnizorLabel = '',
}) {
    const field = fieldKey ? getBulkField(fieldKey) : null;
    const results = [];
    const skipped = [];
    const drafts = [];
    const payloads = [];
    const existingCodes = (items || []).map((item) => item.product_code);

    for (const item of items || []) {
        const built = buildBulkUpdateForItem(item, field, value, {
            priceListIds,
            pricingSettings,
            edits,
            effectiveNote,
            furnizorLabel,
        });
        if (!built.ok) {
            skipped.push({ item, reason: built.reason });
            continue;
        }
        results.push(built);
        payloads.push(built.payload);
        const action = resolveDraftAction(built.payload, existingCodes);
        drafts.push(createDraftEntry({
            action,
            payload: built.payload,
            builderState: built.builderState,
            sourceProductCode: item.product_code,
            previousPrice: item.price,
            updateMode: 'builder',
        }));
    }

    return { results, skipped, drafts, payloads };
}

/**
 * Preview rows for the bulk dialog.
 * @param {Object} params
 * @returns {{ rows: Array<Object>, skipped: Array<Object>, field: Object|null, payloads: Array<Object>, drafts: Array<Object> }}
 */
export function buildBulkPreview({
    items,
    fieldKey,
    value,
    edits = null,
    priceListIds,
    pricingSettings,
    effectiveNote = '',
    furnizorLabel = '',
}) {
    const field = fieldKey ? getBulkField(fieldKey) : (
        Array.isArray(edits) && edits[0]?.fieldKey
            ? getBulkField(edits[0].fieldKey)
            : null
    );
    const { results, skipped, payloads, drafts } = buildBulkPayloads({
        items,
        fieldKey,
        value,
        edits,
        priceListIds,
        pricingSettings,
        effectiveNote,
        furnizorLabel,
    });
    return {
        field,
        rows: results.map((entry) => entry.preview),
        skipped,
        payloads,
        drafts,
        hasMarkupPolicyA: results.some((entry) => entry.hasMarkupPolicyA),
    };
}
